import React, { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";

import { designTokens } from "../theme";

type DashboardTab =
  | "all"
  | "prospective"
  | "existing";

type DashboardTabDefinition = {
  value: DashboardTab;
  label: string;
  description: string;
  icon: React.ReactElement;
};

const dashboardTabs: DashboardTabDefinition[] = [
  {
    value: "all",
    label: "全体",
    description:
      "見込み顧客・既存顧客を含む営業活動全体を確認します。",
    icon: <DashboardRoundedIcon fontSize="small" />,
  },
  {
    value: "prospective",
    label: "見込み顧客",
    description:
      "見込み顧客に対する営業活動と進捗状況を確認します。",
    icon: <PersonSearchRoundedIcon fontSize="small" />,
  },
  {
    value: "existing",
    label: "既存顧客",
    description:
      "既存顧客への対応状況と案件の動きを確認します。",
    icon: <HandshakeRoundedIcon fontSize="small" />,
  },
];

const isDashboardTab = (
  value: string | null,
): value is DashboardTab => {
  return dashboardTabs.some((tab) => tab.value === value);
};

type SummaryCardProps = {
  label: string;
  value: string;
  description: string;
  icon: React.ReactElement;
  accent: string;
  background: string;
};

const SummaryCard = ({
  label,
  value,
  description,
  icon,
  accent,
  background,
}: SummaryCardProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 3,
        position: "relative",
        overflow: "hidden",
        backgroundColor: designTokens.surface.paper,
        border: `1px solid ${designTokens.border.default}`,
        boxShadow: "0 5px 18px rgba(15,23,42,0.045)",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, ${background}, transparent 58%)`,
          pointerEvents: "none",
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Box>
          <Typography
            sx={{
              color: designTokens.text.secondary,
              fontSize: 12.5,
              fontWeight: 800,
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              color: designTokens.text.primary,
              fontSize: 28,
              fontWeight: 900,
              lineHeight: 1.2,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {value}
          </Typography>

          <Typography
            sx={{
              mt: 0.75,
              color: designTokens.text.secondary,
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 42,
            height: 42,
            display: "grid",
            placeItems: "center",
            borderRadius: 2,
            color: accent,
            backgroundColor: background,
            border: `1px solid ${designTokens.border.default}`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
};

const EmptyDashboardPanel = ({
  tab,
}: {
  tab: DashboardTab;
}) => {
  const config = {
    all: {
      title: "全体ダッシュボード",
      description:
        "見込み顧客・既存顧客・案件・日報を横断した集計をここへ配置します。",
    },
    prospective: {
      title: "見込み顧客ダッシュボード",
      description:
        "アプローチ状況、購買意欲、次回対応、失注状況などの集計をここへ配置します。",
    },
    existing: {
      title: "既存顧客ダッシュボード",
      description:
        "対応履歴、継続案件、最終コンタクト日などの集計をここへ配置します。",
    },
  }[tab];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.5,
        minHeight: 300,
        p: { xs: 2.5, md: 4 },
        borderRadius: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        backgroundColor: designTokens.surface.paper,
        border: `1px dashed ${designTokens.border.strong}`,
      }}
    >
      <Stack alignItems="center" spacing={1.2}>
        <Box
          sx={{
            width: 64,
            height: 64,
            display: "grid",
            placeItems: "center",
            borderRadius: 3,
            color: designTokens.brand.dark,
            backgroundColor:
              designTokens.overlay.brandSoft,
            border: `1px solid ${designTokens.overlay.brandMedium}`,
          }}
        >
          <AssessmentRoundedIcon sx={{ fontSize: 34 }} />
        </Box>

        <Typography
          sx={{
            color: designTokens.text.primary,
            fontSize: 17,
            fontWeight: 900,
          }}
        >
          {config.title}
        </Typography>

        <Typography
          sx={{
            maxWidth: 560,
            color: designTokens.text.secondary,
            fontSize: 13,
            lineHeight: 1.75,
          }}
        >
          {config.description}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default function Dashboard() {
  const { isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedTab = useMemo<DashboardTab>(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    return isDashboardTab(tab) ? tab : "all";
  }, [location.search]);

  const selectedDefinition =
    dashboardTabs.find((tab) => tab.value === selectedTab) ??
    dashboardTabs[0];

  const handleTabChange = (
    _: React.SyntheticEvent,
    value: DashboardTab,
  ) => {
    navigate(`/dashboard?tab=${value}`, {
      replace: true,
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background:
          "radial-gradient(circle at top right, rgba(152,188,46,0.07), transparent 28%), #F4F6F8",
      }}
    >
      <Toolbar />

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1480, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(115deg, rgba(255,255,255,0.98), rgba(247,250,242,0.98) 62%, rgba(235,243,225,0.98))",
              border: `1px solid ${designTokens.border.default}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.35,
                pointerEvents: "none",
                backgroundImage:
                  "radial-gradient(rgba(93,116,62,0.14) 0.7px, transparent 0.7px)",
                backgroundSize: "15px 15px",
                maskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.65), transparent 60%)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{
                position: "relative",
                zIndex: 1,
                p: { xs: 1.6, md: 2 },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    borderRadius: 2,
                    color: designTokens.brand.dark,
                    backgroundColor: designTokens.overlay.brandSoft,
                    border: `1px solid ${designTokens.overlay.brandMedium}`,
                  }}
                >
                  <AssessmentRoundedIcon />
                </Box>

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      color: designTokens.text.primary,
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 900,
                    }}
                  >
                    営業ダッシュボード
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: designTokens.text.secondary,
                      fontSize: 13,
                    }}
                  >
                    {selectedDefinition.description}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                size="small"
                label={selectedDefinition.label}
                sx={{
                  color: designTokens.brand.dark,
                  backgroundColor: designTokens.overlay.brandSoft,
                  border: `1px solid ${designTokens.overlay.brandMedium}`,
                  fontWeight: 800,
                }}
              />
            </Stack>

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderTop: `1px solid ${designTokens.border.default}`,
                backgroundColor: "rgba(255,255,255,0.72)",
                overflowX: "auto",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="営業ダッシュボード区分"
                sx={{
                  minHeight: 56,
                  px: { xs: 0.5, md: 1 },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                    backgroundColor: designTokens.brand.dark,
                  },
                  "& .MuiTab-root": {
                    minHeight: 56,
                    minWidth: { xs: 130, sm: 160 },
                    color: designTokens.text.secondary,
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: 2,
                    mx: 0.25,
                    "&:hover": {
                      color: designTokens.brand.dark,
                      backgroundColor: designTokens.overlay.brandSoft,
                    },
                    "&.Mui-selected": {
                      color: designTokens.brand.dark,
                    },
                  },
                }}
              >
                {dashboardTabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    icon={tab.icon}
                    iconPosition="start"
                    label={tab.label}
                  />
                ))}
              </Tabs>
            </Box>
          </Paper>

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <SummaryCard
                label="顧客数"
                value="—"
                description="対象区分に含まれる顧客数"
                icon={<GroupsRoundedIcon />}
                accent={designTokens.brand.dark}
                background={designTokens.overlay.brandSoft}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <SummaryCard
                label="進行中案件"
                value="—"
                description="現在対応中の案件数"
                icon={<WorkRoundedIcon />}
                accent="#6B5FC7"
                background="rgba(107,95,199,0.08)"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <SummaryCard
                label="営業活動"
                value="—"
                description="期間内に登録された営業活動"
                icon={<TrendingUpRoundedIcon />}
                accent={designTokens.state.info}
                background="rgba(50,134,184,0.08)"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <SummaryCard
                label="次回対応"
                value="—"
                description="今後の対応予定がある件数"
                icon={<AssessmentRoundedIcon />}
                accent={designTokens.state.warning}
                background="rgba(217,142,4,0.08)"
              />
            </Grid>
          </Grid>

          <EmptyDashboardPanel tab={selectedTab} />
        </Box>
      </Box>
    </Box>
  );
}